import { useI18n } from "@core/i18n/i18n";
import { onboardingManager, libraryFilterManager } from "@core/service-container";
import { BaseExercise, MainExercise } from "@domain/exercise/exercise";
import {
	AnalyticsViewExerciseDetailOrigin,
	sendAnalyticsEventDeleteAlternativeExercises,
} from "@domain/library/exercise-analytics";
import { getLocaleDayColumnIndex } from "@domain/program/program";
import { OnboardingProgramQuickstartStep } from "@domain/onboarding/onboarding";
import { ProgramContentEditor } from "@domain/program/program-content-editor";
import { DayIndex } from "@domain/program/program-days";

import styled from "@emotion/styled";
import { ResponsiveDeviceType } from "@ui/hooks/use-dimensions";
import { DraggableAlternativeExerciseCard } from "@ui/pages/program-builder/alternative-exercise/alternative-exercise-card";
import { MAX_ALTERNATIVE_EXERCISES } from "@ui/pages/program-builder/alternative-exercise/alternative-exercises-utils";
import { AlternativeError } from "@ui/pages/program-builder/alternative-exercise/components/alternative-error";
import {
	AlternativeExerciseHeader,
	AlternativeExerciseHeaderHeight,
} from "@ui/pages/program-builder/alternative-exercise/components/alternative-exercise-header";
import {
	NO_ALTERNATIVE_PLACEHOLDER_HEIGHT,
	NoAlternativeExercisePlaceholder,
} from "@ui/pages/program-builder/alternative-exercise/components/no-alternative-exercise-placeholder";
import { ConditionalAnimateSharedLayout, DAY_COLUMN_WIDTH } from "@ui/pages/program-builder/day-table/day-column";
import { DAY_GAP } from "@ui/pages/program-builder/day-table/day-table";
import { ExerciseDetailsDialogTabs } from "@ui/pages/program-builder/dialog/exercise-details-dialog";
import { DROPPABLE_ID } from "@ui/pages/program-builder/drag-drop/program-builder-drag-drop-context";
import { useEditorExerciseCardActions } from "@ui/pages/program-builder/hooks/use-editor-exercise-card-actions";
import { useExerciseDragAndDrop } from "@ui/pages/program-builder/hooks/use-exercise-drag-and-drop";
import { ExerciseCard } from "@ui/pages/program-builder/library/exercise-card";
import { stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { getScrollbarStyle } from "@ui/style/scrollbar";
import { isNotNil } from "@utils/assert";
import { motion } from "framer-motion";
import { useMemoizedObservable, useObservable } from "micro-observables";
import { alternativePanelQuickstartGuideRequested, clearAlternativePanelQuickstartGuide, alternativeGuide3WasCompleted, hideAlternativeGuide3, setAlternativeGuide3WasCompleted } from "@ui/pages/program-builder/quickstart/alternative-highlight-signal";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Droppable } from "@hello-pangea/dnd";

interface AlternativeExercisePanelProps {
	exercise: MainExercise;
	programEditor: ProgramContentEditor;
	onClose: () => void;
	scrollContainerRef: React.RefObject<HTMLDivElement>;
}

const PANEL_WIDTH = 308;
const PANEL_MARGIN = 16;
const ONBOARDING_PANEL_MARGIN = 8;

const computeEmptyPanelHeight = (): number => {
	const separatorHeight = 2 + 2 * 12;
	const exerciseCardWithoutRepsHeight = 87.5;
	return (
		AlternativeExerciseHeaderHeight +
		exerciseCardWithoutRepsHeight +
		separatorHeight +
		NO_ALTERNATIVE_PLACEHOLDER_HEIGHT +
		12
	);
};

const computePanelHeight = (alternativesLength: number, onboardingPanelHeight: number): number => {
	const exerciseCardHeight = 140;
	return (
		computeEmptyPanelHeight() +
		(8 + exerciseCardHeight) * alternativesLength +
		onboardingPanelHeight +
		ONBOARDING_PANEL_MARGIN
	);
};

export const AlternativeExercisePanel: React.FC<AlternativeExercisePanelProps> = ({
	exercise,
	programEditor,
	scrollContainerRef,
}) => {
	useI18n();
	const { shouldAnimate } = useExerciseDragAndDrop();
	const error = useMemoizedObservable(
		() => programEditor.alternativeError,

		[programEditor],
	);
	const editedExercise = useObservable(libraryFilterManager.exerciseWithAlternativesEdited);

	const mainAndAlternativeExercises = [exercise, ...exercise.alternativeExercises];
	const topOnboardingPanelRef = useRef<HTMLDivElement>(null);
	const [topOnboardingPanelHeight, setTopOnboardingPanelHeight] = useState(0);
	const bottomOnboardingPanelRef = useRef<HTMLDivElement>(null);
	const [bottomOnboardingPanelHeight, setBottomOnboardingPanelHeight] = useState(0);

	useLayoutEffect(() => {
		if (isNotNil(topOnboardingPanelRef.current)) {
			const { height } = topOnboardingPanelRef.current.getBoundingClientRect();
			setTopOnboardingPanelHeight(height);
		}
		if (isNotNil(bottomOnboardingPanelRef.current)) {
			const { height } = bottomOnboardingPanelRef.current.getBoundingClientRect();
			setBottomOnboardingPanelHeight(height);
		}
	}, []);

	const getAlternativePanelPosition = useCallback(
		(i?: number) => {
			const columnIndex = (i ?? 0) % 7;
			return (
				(columnIndex + 1) * DAY_GAP +
				(columnIndex + 0.5) * DAY_COLUMN_WIDTH -
				PANEL_WIDTH / 2 -
				(scrollContainerRef.current?.scrollLeft ?? 0)
			);
		},
		[scrollContainerRef],
	);

 

	const panelPosition = useMemo(() => {
		const cardElement = document.getElementById(editedExercise?.draggableId ?? "");
		if (isNotNil(editedExercise) && isNotNil(cardElement)) {
			return {
				panelLeftPosition: getAlternativePanelPosition(
					getLocaleDayColumnIndex(
						programEditor.getExerciseIndex(editedExercise.draggableId)?.sessionIndex ?? DayIndex.SUNDAY,
					),
				),
				cardTopPosition: document.getElementById(editedExercise.draggableId)?.getBoundingClientRect().top ?? 0,
			};
		}
		return null;
	}, [editedExercise, getAlternativePanelPosition, programEditor]);

	const startPosition =
		(panelPosition?.cardTopPosition ?? 0) -
		AlternativeExerciseHeaderHeight -
		(scrollContainerRef.current?.getBoundingClientRect().top ?? 0);

	const updateAlternativeFromEditedExercise = useCallback(
		(updatedExercise: BaseExercise) => {
			programEditor.updateAlternativeFromEditedExercise(updatedExercise);
		},
		[programEditor],
	);

	const removeAlternative = useCallback(
		(deletedAlternative: BaseExercise) => {
			sendAnalyticsEventDeleteAlternativeExercises(exercise, deletedAlternative);
			programEditor.removeAlternativeFromExercise(deletedAlternative);
		},
		[programEditor, exercise],
	);

	const { openExerciseDetailsModal } = useEditorExerciseCardActions();

	const shouldShowQuickstartTip = useObservable(alternativePanelQuickstartGuideRequested);
	const showGuide04 = useObservable(alternativeGuide3WasCompleted);

	useEffect(() => {
		if (showGuide04 && shouldShowQuickstartTip) {
			clearAlternativePanelQuickstartGuide();
		}
	}, [showGuide04, shouldShowQuickstartTip]);

	// Auto-close the quickstart tip when an alternative is added
	const previousAlternativesCountRef = useRef<number>(exercise.alternativeExercises.length);
		useEffect(() => {
		const currentCount = exercise.alternativeExercises.length;
		const previousCount = previousAlternativesCountRef.current;
		if (shouldShowQuickstartTip && currentCount > previousCount) {
				void (async () => {
					await onboardingManager.passStepOnboarding(
						OnboardingProgramQuickstartStep.ALTERNATIVE_EXERCISES_GUIDE_02,
					);
					await onboardingManager.passStepOnboarding(
						OnboardingProgramQuickstartStep.ALTERNATIVE_EXERCISES_GUIDE_03,
					);
					await onboardingManager.init();
					clearAlternativePanelQuickstartGuide();
					hideAlternativeGuide3();
					setAlternativeGuide3WasCompleted();
				})();
		}
		previousAlternativesCountRef.current = currentCount;
	}, [exercise.alternativeExercises.length, shouldShowQuickstartTip]);

	const endPosition = useMemo(() => {
		const columnHeight = (scrollContainerRef.current?.clientHeight ?? 0) + 22;
		const initCardHeight = computeEmptyPanelHeight();
		const panelHeight = computePanelHeight(
			exercise.alternativeExercises.length,
			topOnboardingPanelHeight + bottomOnboardingPanelHeight,
		);

		const maxCardHeight = (columnHeight + initCardHeight) / 2 - PANEL_MARGIN;

		if (panelHeight < maxCardHeight) {
			return (columnHeight - initCardHeight) / 2;
		} else {
			return Math.max(columnHeight - panelHeight - 2 * PANEL_MARGIN, PANEL_MARGIN);
		}
	}, [scrollContainerRef, exercise.alternativeExercises.length, topOnboardingPanelHeight, bottomOnboardingPanelHeight]);

	const animatedStyle = {
		hidden: {
			top: startPosition,
			left: panelPosition?.panelLeftPosition,
			opacity: 0,
		},
		visible: {
			opacity: 1,
			top: endPosition,
			left: panelPosition?.panelLeftPosition,
			transition: {
				type: "spring",
				stiffness: 150,
				damping: 15,
			},
		},
		exit: {
			scale: 0.2,
			translateY: 158,
			left: panelPosition?.panelLeftPosition,
			opacity: 0,
			transition: {
				duration: 0.54,
				ease: "easeInOut",
			},
		},
	};

	return (
		<Container
			variants={animatedStyle}
			initial="hidden"
			animate="visible"
			exit="exit"
			onClick={e => e.stopPropagation()}
		>
			<AlternativeExerciseHeader
				onCrossClicked={async () => {
					programEditor.applyChangesAndResetEditedExercise();
					await libraryFilterManager.clearLibraryFiltersAndSearch();
					libraryFilterManager.clearActiveFilteringState();
				}}
			/>
			<AlternativeError errorType={error} cleanError={() => programEditor.cleanAlternativeExerciseError()} />
			<ExerciseCard
				exercise={exercise}
				deviceType={ResponsiveDeviceType.Desktop}
				showAlternatives={false}
				onlyAllowPreview
				hideContent
				onClick={() =>
					openExerciseDetailsModal(exercise, mainAndAlternativeExercises, AnalyticsViewExerciseDetailOrigin.CANVAS)
				}
				onCommentIconClick={() =>
					openExerciseDetailsModal(
						exercise,
						mainAndAlternativeExercises,
						AnalyticsViewExerciseDetailOrigin.CANVAS,
						ExerciseDetailsDialogTabs.COMMENT,
					)
				}
			/>
			<Separator />
			<Droppable droppableId={DROPPABLE_ID.Alternative}>
				{(provided, snapshot) => (
					<AlternativeExercisesList ref={provided.innerRef} id={DROPPABLE_ID.Alternative} {...provided.droppableProps}>
						<ConditionalAnimateSharedLayout active={shouldAnimate}>
							{exercise.alternativeExercises.map((ex, exerciseIndex, alternatives) => (
								<DraggableAlternativeExerciseCard
									className="not-provided-placeholder"
									key={ex.draggableId}
									alternativeExercise={ex}
									relatedExercises={mainAndAlternativeExercises}
									index={exerciseIndex}
									shouldAnimate={shouldAnimate}
									alternatives={alternatives}
									alternativeExerciseIndex={exerciseIndex}
									updateExercise={updateAlternativeFromEditedExercise}
									onDelete={removeAlternative}
								/>
							))}
						</ConditionalAnimateSharedLayout>
						{provided.placeholder}
						{exercise.alternativeExercises.length < MAX_ALTERNATIVE_EXERCISES && (
							<NoAlternativeExercisePlaceholder
								onClick={() => libraryFilterManager.setSidebarVisible(true)}
								display={!snapshot.isDraggingOver}
							/>
						)}
					</AlternativeExercisesList>
				)}
			</Droppable>

		</Container>
	);
};

const Container = styled(motion.div)`
	position: absolute;
	width: ${PANEL_WIDTH}px;
	padding: 0 12px 12px 12px;
	background-color: ${colors.primary10};
	border-radius: 16px;
	max-height: calc(100% - 2 * 20px);
	z-index: 0;
	overflow-y: auto;
	${getScrollbarStyle(6)};
`;

const Separator = styled.div`
	margin: 12px 0;
	height: 2px;
	background-color: ${colors.primary25};
`;

const AlternativeExercisesList = styled.div`
	${stack(8, "flexstart", "stretch")};
`;

